/**
 * Strict Parameter Validation Utility
 * Enforces parameters.md as the single source of truth with zero fallback mechanisms
 */

import { EXPECTED_PARAMETER_COUNT } from './parameterCountValidator';

/**
 * Validates that configuration files strictly align with parameters.md
 * @param {Array} configuredParameters - Parameters from configuration files
 * @param {Array} parametersMdParameters - Parameters from parameters.md
 * @returns {Object} Validation results with strict enforcement
 */
export const validateStrictParameterAlignment = (configuredParameters, parametersMdParameters) => {
  const configuredSet = new Set(configuredParameters);
  const parametersMdSet = new Set(parametersMdParameters);
  
  const extraInConfig = configuredParameters.filter(param => !parametersMdSet.has(param));
  const missingFromConfig = parametersMdParameters.filter(param => !configuredSet.has(param));
  
  const isStrictlyAligned = extraInConfig.length === 0 && missingFromConfig.length === 0;
  const hasCorrectCount = configuredParameters.length === EXPECTED_PARAMETER_COUNT;
  
  return {
    isStrictlyAligned,
    hasCorrectCount,
    configuredCount: configuredParameters.length,
    expectedCount: EXPECTED_PARAMETER_COUNT,
    extraInConfig,
    missingFromConfig,
    extraCount: extraInConfig.length,
    missingCount: missingFromConfig.length,
    errors: [],
    warnings: []
  };
};

/**
 * Validates parameter naming conventions against parameters.md standards
 * @param {string} parameterKey - Parameter key to validate
 * @returns {Object} Validation result
 */
export const validateParameterNaming = (parameterKey) => {
  const namingPattern = /^[a-z]+_[a-zA-Z0-9_]+$/;
  const validCategories = ['vitals', 'labs', 'biometrics', 'pe', 'imaging', 'lifestyle'];
  
  if (!parameterKey || typeof parameterKey !== 'string') {
    return {
      isValid: false,
      error: 'Parameter key must be a non-empty string'
    };
  }
  
  if (!namingPattern.test(parameterKey)) {
    return {
      isValid: false,
      error: 'Parameter key must follow format: category_subcategory_parameter_name (lowercase with underscores)'
    };
  }
  
  const category = parameterKey.split('_')[0];
  if (!validCategories.includes(category)) {
    return {
      isValid: false,
      error: `Invalid category '${category}'. Must be one of: ${validCategories.join(', ')}`
    };
  }
  
  return {
    isValid: true,
    category
  };
};

/**
 * Validates that no fallback mechanisms are being used
 * @param {Object} parameterConfig - Parameter configuration object
 * @returns {Object} Validation result
 */
export const validateNoFallbackMechanisms = (parameterConfig) => {
  const fallbackIndicators = [
    'fallback',
    'default',
    'optional',
    'backup',
    'alternative',
    '_synthetic',
    'generated'
  ];
  
  const errors = [];
  
  // Check for fallback indicators in configuration
  const configString = JSON.stringify(parameterConfig).toLowerCase();
  fallbackIndicators.forEach(indicator => {
    if (configString.includes(indicator)) {
      errors.push(`Configuration contains fallback mechanism indicator: '${indicator}'`);
    }
  });
  
  // Check for optional chaining with fallback values
  if (parameterConfig.target_source === 'fallback' || 
      parameterConfig.target_source === 'default') {
    errors.push('Target source cannot use fallback or default values');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Comprehensive strict validation for the entire parameter system
 * @param {Object} systemData - Complete system data including configs and parameters
 * @returns {Object} Comprehensive validation results
 */
export const runStrictSystemValidation = (systemData) => {
  const {
    configuredParameters = [],
    parametersMdParameters = [],
    allScoringConfig = [],
    parameterWeights = {},
    targetFormulas = []
  } = systemData;
  
  const results = {
    isSystemValid: true,
    alignmentValidation: null,
    namingValidation: { valid: [], invalid: [] },
    fallbackValidation: { valid: [], invalid: [] },
    configurationCompleteness: { complete: [], incomplete: [] },
    criticalErrors: [],
    warnings: []
  };
  
  // 1. Validate strict alignment
  results.alignmentValidation = validateStrictParameterAlignment(
    configuredParameters, 
    parametersMdParameters
  );
  
  if (!results.alignmentValidation.isStrictlyAligned) {
    results.isSystemValid = false;
    results.criticalErrors.push('Configuration files do not strictly align with parameters.md');
  }
  
  // 2. Validate parameter naming
  configuredParameters.forEach(paramKey => {
    const namingResult = validateParameterNaming(paramKey);
    if (namingResult.isValid) {
      results.namingValidation.valid.push(paramKey);
    } else {
      results.namingValidation.invalid.push({ paramKey, error: namingResult.error });
      results.isSystemValid = false;
    }
  });
  
  // 3. Validate no fallback mechanisms
  allScoringConfig.forEach(config => {
    const fallbackResult = validateNoFallbackMechanisms(config);
    if (fallbackResult.isValid) {
      results.fallbackValidation.valid.push(config.key);
    } else {
      results.fallbackValidation.invalid.push({ 
        paramKey: config.key, 
        errors: fallbackResult.errors 
      });
      results.isSystemValid = false;
    }
  });
  
  // 4. Validate configuration completeness
  parametersMdParameters.forEach(paramKey => {
    const hasScoring = allScoringConfig.some(config => config.key === paramKey);
    const hasWeights = parameterWeights.parameters && parameterWeights.parameters[paramKey];
    
    if (hasScoring && hasWeights) {
      results.configurationCompleteness.complete.push(paramKey);
    } else {
      results.configurationCompleteness.incomplete.push({
        paramKey,
        missingScoring: !hasScoring,
        missingWeights: !hasWeights
      });
      results.warnings.push(`Parameter ${paramKey} has incomplete configuration`);
    }
  });
  
  return results;
};

/**
 * Logs strict validation results with appropriate severity levels
 * @param {Object} validationResults - Results from runStrictSystemValidation
 */
export const logStrictValidationResults = (validationResults) => {
  console.log('🔒 STRICT PARAMETER VALIDATION RESULTS');
  console.log('='.repeat(80));
  
  if (validationResults.isSystemValid) {
    console.log('✅ SYSTEM VALIDATION PASSED: All strict requirements met');
  } else {
    console.error('❌ SYSTEM VALIDATION FAILED: Critical issues detected');
    console.error('🚫 STRICT MODE: System will not operate with fallback mechanisms');
  }
  
  // Log alignment results
  const alignment = validationResults.alignmentValidation;
  if (alignment.isStrictlyAligned) {
    console.log(`✅ ALIGNMENT: Perfect 1:1 mapping (${alignment.configuredCount} parameters)`);
  } else {
    console.error(`❌ ALIGNMENT: ${alignment.extraCount} extra, ${alignment.missingCount} missing`);
  }
  
  // Log naming validation
  const naming = validationResults.namingValidation;
  console.log(`📝 NAMING: ${naming.valid.length} valid, ${naming.invalid.length} invalid`);
  if (naming.invalid.length > 0) {
    console.error('Invalid parameter names:', naming.invalid.slice(0, 5));
  }
  
  // Log fallback validation
  const fallback = validationResults.fallbackValidation;
  console.log(`🚫 FALLBACKS: ${fallback.valid.length} clean, ${fallback.invalid.length} with fallbacks`);
  if (fallback.invalid.length > 0) {
    console.error('Parameters with fallback mechanisms:', fallback.invalid.slice(0, 3));
  }
  
  // Log configuration completeness
  const completeness = validationResults.configurationCompleteness;
  console.log(`⚙️ COMPLETENESS: ${completeness.complete.length} complete, ${completeness.incomplete.length} incomplete`);
  
  if (validationResults.criticalErrors.length > 0) {
    console.error('🚨 CRITICAL ERRORS:');
    validationResults.criticalErrors.forEach(error => console.error(`  - ${error}`));
  }
  
  if (validationResults.warnings.length > 0) {
    console.warn(`⚠️ WARNINGS: ${validationResults.warnings.length} configuration warnings`);
  }
  
  console.log('='.repeat(80));
};
